import React, { useContext, useEffect, useState } from 'react';
import { ProcessCritiqueHook } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useProcessCritique';
import { ProcessSupervisionChunk } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/types';
import ProcessCritiqueAlert from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/ProcessCritique/ProcessCritiqueAlert';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { Button } from '@scale/scaleui';
import { ReviewLevel } from '@scale/shared-types';

interface Props {
  critiqueKey: string;
  processCritiqueHook: ProcessCritiqueHook;
  mostRecentPrompt?: string;
  enabledReviewLevels?: ReviewLevel[];
  processSteps: ProcessSupervisionChunk[];
  hasGtfa?: boolean;
}

function ProcessCritique(props: Props) {
  const {
    critiqueKey,
    processCritiqueHook,
    mostRecentPrompt,
    processSteps,
    enabledReviewLevels = [],
    hasGtfa = true,
  } = props;

  const { critiqueData, getProcessCritique } = processCritiqueHook;

  const { loading, error: processCritiqueError } = critiqueData;

  const taskContext = useContext(ChatTaskContext);
  const config = useChatStoreExtract(state => state.config);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);

  const [groundTruth, setGroundTruth] = useState<string | undefined>(undefined);

  useEffect(() => {
    function findValueByKey(obj: Record<string, any>, keyToFind: string): string | undefined {
      let foundValue: string | undefined = undefined;

      for (const key in obj) {
        if (key === keyToFind) {
          return obj[key];
        } else if (typeof obj[key] === 'object') {
          foundValue = findValueByKey(obj[key], keyToFind);
          if (foundValue) {
            return foundValue;
          }
        }
      }
      return foundValue;
    }

    // Find the ground truth value in the config, auto run the process critique if it exists
    const groundTruthValue = findValueByKey(config, critiqueKey);
    setGroundTruth(groundTruthValue);
  }, [config, setGroundTruth, critiqueKey]);

  useEffect(() => {
    const callProcessCritique = async function (groundTruth?: string): Promise<void> {
      await getProcessCritique({
        // Adding an additional check to ensure that the ground truth should be undefined if hasGtfa is false
        groundTruth: hasGtfa ? groundTruth : undefined,
        mostRecentPrompt: mostRecentPrompt ?? 'n/a',
        processSteps,
        taskId: taskContext.taskId ?? 'MISSING_TASKID',
      });
    };

    if ((groundTruth || !hasGtfa) && processSteps.length > 0 && critiqueData.hasRun === false) {
      callProcessCritique(groundTruth);
    }
  }, [
    groundTruth,
    processSteps,
    critiqueData.hasRun,
    getProcessCritique,
    mostRecentPrompt,
    taskContext.taskId,
    hasGtfa,
  ]);

  const showProcessCritique =
    enabledReviewLevels.length === 0 || enabledReviewLevels.includes(reviewLevel);

  if (!showProcessCritique) {
    return null;
  }

  return (
    <div>
      {!loading ? (
        <div className="flex mb-2 items-center justify-between">
          <div className="flex-grow mr-3 ">
            {hasGtfa ? (
              <textarea
                className="block w-full rounded-md py-2 text-neutral-900 sm:text-sm shadow-sm border-1 placeholder:text-neutral-400 transition outline-none bg-neutral-0 min-h-[38px] focus:border-primary-400 focus:ring-primary-400 border-neutral-300"
                placeholder="Enter the answer here"
                value={groundTruth}
                onChange={e => setGroundTruth(e.target.value)}
                rows={1}
              ></textarea>
            ) : null}
          </div>
          <Button
            className="py-2 px-10 w-30 h-full w-fit whitespace-nowrap"
            variant="secondary"
            onClick={() => {
              getProcessCritique({
                groundTruth: groundTruth ?? '',
                mostRecentPrompt: mostRecentPrompt ?? 'n/a',
                processSteps,
                taskId: taskContext.taskId ?? 'MISSING_TASKID',
              });
            }}
            disabled={loading}
          >
            Rerun Process Critique
          </Button>
        </div>
      ) : null}
      <div hidden={!processCritiqueError} className="text-danger-400">
        Something went wrong when running process critique. Please try again.
      </div>
      <div className="mt-3">
        <ProcessCritiqueAlert
          processCritiqueOutput={processCritiqueHook.critiqueData}
          hasGtfa={hasGtfa}
        ></ProcessCritiqueAlert>
      </div>
    </div>
  );
}

export default ProcessCritique;
